import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';

/** A page number to render, or null for an ellipsis between two runs. */
export type PageSlot = number | null;

/**
 * The one pagination control, replacing four near-identical copies that had been
 * pasted between cases, partners and both paginated tabs of partner-details.
 *
 * The copies rendered one button per page — `getTotalPagesArray()` mapped the
 * whole range — so a filter matching a few thousand cases produced a few hundred
 * buttons and the row wrapped down the screen. This windows to at most seven
 * slots, and on a phone drops to Prev / Page N of M / Next, which is three
 * 44px targets that always fit.
 *
 * Pages are 0-based, matching every caller and the backend's `page` parameter.
 */
@Component({
  selector: 'app-pagination',
  templateUrl: './pagination.component.html',
  styleUrls: ['./pagination.component.scss']
})
export class PaginationComponent implements OnChanges {

  @Input() currentPage = 0;
  @Input() pageSize = 10;
  @Input() totalElements = 0;
  @Input() totalPages = 0;
  /** Plural noun for the summary line: "51–75 of 412 cases". */
  @Input() itemLabel = 'items';
  /** Dims the control while a fetch for a new page is in flight. */
  @Input() loading = false;

  @Output() pageChange = new EventEmitter<number>();

  slots: PageSlot[] = [];

  ngOnChanges(): void {
    this.slots = this.buildWindow();
  }

  get from(): number {
    return this.totalElements === 0 ? 0 : this.currentPage * this.pageSize + 1;
  }

  get to(): number {
    return Math.min((this.currentPage + 1) * this.pageSize, this.totalElements);
  }

  get hasPrevious(): boolean {
    return this.currentPage > 0;
  }

  get hasNext(): boolean {
    return this.currentPage < this.totalPages - 1;
  }

  go(page: number): void {
    if (page === this.currentPage || page < 0 || page >= this.totalPages) {
      return;
    }

    this.pageChange.emit(page);
  }

  previous(): void {
    if (this.hasPrevious) {
      this.pageChange.emit(this.currentPage - 1);
    }
  }

  next(): void {
    if (this.hasNext) {
      this.pageChange.emit(this.currentPage + 1);
    }
  }

  trackBySlot(index: number): number {
    return index;
  }

  /**
   * At most seven slots: first, an ellipsis, the page either side of the current
   * one, another ellipsis, last. Runs of two or fewer hidden pages are printed
   * in full rather than replaced by an ellipsis that saves nothing.
   */
  private buildWindow(): PageSlot[] {
    const total = this.totalPages;

    if (total <= 1) {
      return [];
    }

    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i);
    }

    const current = Math.min(Math.max(this.currentPage, 0), total - 1);
    const first = 0;
    const last = total - 1;
    const windowStart = Math.max(first + 1, current - 1);
    const windowEnd = Math.min(last - 1, current + 1);

    const slots: PageSlot[] = [first];

    if (windowStart > first + 1) {
      slots.push(null);
    }

    for (let page = windowStart; page <= windowEnd; page++) {
      slots.push(page);
    }

    if (windowEnd < last - 1) {
      slots.push(null);
    }

    slots.push(last);

    return slots;
  }
}
